import type React from "react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { client } from "@duress/shared";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTierThresholds, usePutTierThresholds } from "./useClients";

interface Props {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FIELDS: {
  name: keyof client.TierThresholds;
  label: string;
  hint: string;
}[] = [
  {
    name: "high_extended_rate",
    label: "High Priority — extended rate ≥",
    hint: "Teams whose extended session rate is at or above this land in High Priority.",
  },
  {
    name: "high_fleet_idle_rate",
    label: "High Priority — fleet idle rate ≥",
    hint: "Teams whose fleet idle rate is at or above this land in High Priority, even with a low extended rate.",
  },
  {
    name: "watch_list_extended_rate",
    label: "Watch List — extended rate ≥",
    hint: "Below the High threshold, teams at or above this go on the Watch List.",
  },
  {
    name: "watch_list_fleet_idle_rate",
    label: "Watch List — fleet idle rate ≥",
    hint: "Below the High idle threshold, teams at or above this go on the Watch List.",
  },
  {
    name: "on_track_extended_rate",
    label: "On Track — extended rate ≥",
    hint: "Below Watch List, teams at or above this are On Track.",
  },
  {
    name: "on_track_fleet_idle_rate",
    label: "On Track — fleet idle rate ≥",
    hint: "Below Watch List, teams at or above this are On Track. Below every threshold above is Excellent.",
  },
];

export function TierSettingsDialog({
  clientId,
  open,
  onOpenChange,
}: Props): React.JSX.Element {
  const { data: current, isLoading } = useTierThresholds(clientId);
  const save = usePutTierThresholds(clientId);

  const form = useForm<client.TierThresholds>({
    resolver: zodResolver(client.TierThresholdsSchema),
    defaultValues: { ...client.TIER_THRESHOLD_DEFAULTS },
  });

  // Populate the form when the dialog opens or fetched values arrive.
  useEffect(() => {
    if (open && current) form.reset(current);
  }, [open, current, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    await save.mutateAsync(values);
    onOpenChange(false);
  });

  const resetToDefaults = (): void => {
    form.reset({ ...client.TIER_THRESHOLD_DEFAULTS });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogHeader>
        <DialogTitle>Tier thresholds</DialogTitle>
        <DialogDescription>
          Configure where the boundary between each tier sits. Changes apply to
          all existing periods immediately — no re-upload needed.
        </DialogDescription>
      </DialogHeader>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <form
          onSubmit={(e) => {
            void onSubmit(e);
          }}
          className="space-y-4"
        >
          {FIELDS.map((field) => {
            const err = form.formState.errors[field.name];
            return (
              <div key={field.name} className="space-y-1.5">
                <Label htmlFor={field.name}>{field.label}</Label>
                <div className="flex items-center gap-2">
                  <Input
                    id={field.name}
                    type="number"
                    min={0}
                    max={100}
                    step="0.1"
                    {...form.register(field.name, { valueAsNumber: true })}
                    className="w-28"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                <p className="text-xs text-muted-foreground">{field.hint}</p>
                {err ? (
                  <p className="text-xs text-destructive">{err.message}</p>
                ) : null}
              </div>
            );
          })}

          {save.error ? (
            <p className="text-sm text-destructive">
              Couldn’t save thresholds. Try again.
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={resetToDefaults}
              disabled={save.isPending}
            >
              Reset to defaults
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
              disabled={save.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      )}
    </Dialog>
  );
}
